/**
 * SoleadLab 2.0 - PV Fault Impact Calculator
 *
 * Floating panel that calculates energy losses (kWh) and revenue losses ($)
 * from PV faults. Supports "Sync from Legend" to auto-populate fault counts
 * from the current project indications, plus CSV/JSON export.
 */

import { useState, useMemo } from 'react';
import { useProjectStore } from '../../stores';
import { INDICATION_TYPES } from '../../stores/projectStore';
import './CalculatorPanel.css';

// ── Types ────────────────────────────────────────────────────────────────────

interface FaultRow {
  id: string;
  label: string;
  count: number;
  category: 'Dense' | 'Availability' | 'Risk Only';
  scope: 'Module' | 'String';
  impact: 'L' | 'ML' | 'H';
  color: string;
}

interface CalculatorPanelProps {
  onClose: () => void;
}

// ── Constants ────────────────────────────────────────────────────────────────

const SR_MAP: Record<string, number> = {
  L:  0.05,  // Minor: soiling, minor cracks (~2-5% per IEC 61724 PR factors)
  ML: 0.33,  // Moderate-Loss: 1 bypassed substring = 33.3% (IEA-PVPS)
  H:  1.00,  // Full: string OC / module OC = 100% (IEC 62446-3)
};

const CATEGORIES: FaultRow['category'][] = ['Dense', 'Availability', 'Risk Only'];
const SCOPES: FaultRow['scope'][] = ['Module', 'String'];
const IMPACTS: FaultRow['impact'][] = ['L', 'ML', 'H'];
const CURRENCIES = ['$', '\u20AC', '\u20AA'] as const; // $, EUR, ILS
const MODULE_TYPES = ['mono', 'poly', 'thin-film'] as const;
const TIME_UNITS = ['days', 'months', 'years'] as const;
type TimeUnit = typeof TIME_UNITS[number];

/** Convert a display period (value + unit) to fractional years */
function periodToYears(value: number, unit: TimeUnit): number {
  if (unit === 'years') return value;
  if (unit === 'months') return value / 12;
  return value / 365; // days
}

/** Human-readable period label, e.g. "6 months" or "1 year" */
function periodLabel(value: number, unit: TimeUnit): string {
  if (value === 1) {
    if (unit === 'days') return 'day';
    if (unit === 'months') return 'month';
    return 'year';
  }
  return `${value} ${unit}`;
}

const DEFAULT_FAULTS: FaultRow[] = [
  { id: 'Diode',     label: 'Diode',     count: 0, category: 'Dense',        scope: 'Module', impact: 'ML', color: '#ff0000' },
  { id: 'Hot spots', label: 'Hot spots', count: 0, category: 'Dense',        scope: 'Module', impact: 'ML', color: '#ffd400' },
  { id: 'Crack',     label: 'Crack',     count: 0, category: 'Dense',        scope: 'Module', impact: 'L',  color: '#7ecbff' },
  { id: 'String OC', label: 'String OC', count: 0, category: 'Availability', scope: 'String', impact: 'H',  color: '#00a651' },
  { id: 'Glass',     label: 'Glass',     count: 0, category: 'Dense',        scope: 'Module', impact: 'ML', color: '#ff8c00' },
  { id: 'PID',       label: 'PID',       count: 0, category: 'Dense',        scope: 'Module', impact: 'ML', color: '#0047ab' },
  { id: 'O.circuit', label: 'O.circuit', count: 0, category: 'Availability', scope: 'Module', impact: 'H',  color: '#00a651' },
  { id: 'Opt.OC',    label: 'Opt.OC',    count: 0, category: 'Availability', scope: 'Module', impact: 'H',  color: '#00a651' },
  { id: 'Opt.SC',    label: 'Opt.SC',    count: 0, category: 'Availability', scope: 'Module', impact: 'H',  color: '#20c997' },
  { id: 'Riso/Pol',  label: 'Riso/Pol',  count: 0, category: 'Dense',        scope: 'Module', impact: 'ML', color: '#7d3cff' },
  { id: 'Soiling',   label: 'Soiling',   count: 0, category: 'Dense',        scope: 'Module', impact: 'L',  color: '#8b4513' },
  { id: 'EL',        label: 'EL',        count: 0, category: 'Risk Only',    scope: 'Module', impact: 'L',  color: '#666666' },
  { id: 'Custom',    label: 'Custom',    count: 0, category: 'Dense',        scope: 'Module', impact: 'L',  color: '#999999' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatNum(n: number, decimals = 0): string {
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function downloadBlob(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Component ────────────────────────────────────────────────────────────────

export default function CalculatorPanel({ onClose }: CalculatorPanelProps) {
  // ── System Config ──
  const [dcCapacityKWp, setDcCapacityKWp] = useState(1000);
  const [expectedYield, setExpectedYield] = useState(1600);

  // ── Module Config ──
  const [modulePowerWp, setModulePowerWp] = useState(550);
  const [cellsPerModule, setCellsPerModule] = useState(144);
  const [bypassDiodes, setBypassDiodes] = useState(3);
  const [moduleType, setModuleType] = useState<string>('mono');

  // ── Topology ──
  const [totalModules, setTotalModules] = useState(1818);
  const [stringsCount, setStringsCount] = useState(66);
  const [mpptCount, setMpptCount] = useState(33);
  const [inverterCount, setInverterCount] = useState(3);
  const [modsPerOptimizer, setModsPerOptimizer] = useState(1);

  // ── Financial ──
  const [tariff, setTariff] = useState(0.08);
  const [currency, setCurrency] = useState('$');

  // ── Display Period ──
  const [periodValue, setPeriodValue] = useState(1);
  const [periodUnit, setPeriodUnit] = useState<TimeUnit>('years');
  const periodYears = periodToYears(periodValue, periodUnit);
  const periodStr = periodLabel(periodValue, periodUnit);

  // ── Faults ──
  const [faults, setFaults] = useState<FaultRow[]>(DEFAULT_FAULTS);
  const [selectedFaultIndex, setSelectedFaultIndex] = useState<number | null>(null);

  // ── Project data for Sync from Legend ──
  const indications = useProjectStore((s) => s.data.indications);

  // ── Calculations ──
  const results = useMemo(() => {
    const E_total = dcCapacityKWp * expectedYield;
    const E_per_module = (modulePowerWp / 1000) * expectedYield;
    const modulesPerString = stringsCount > 0 ? totalModules / stringsCount : 0;
    const E_per_string = E_per_module * modulesPerString;

    const faultResults = faults.map((fault) => {
      if (fault.count === 0 || fault.category === 'Risk Only') {
        return { ...fault, energyLossKWh: 0, revenueLoss: 0 };
      }

      const SR = SR_MAP[fault.impact] ?? 0;
      const n = fault.count;
      const isOptimizer = fault.id === 'Opt.OC' || fault.id === 'Opt.SC';
      const E_base = fault.scope === 'String' ? E_per_string
        : isOptimizer ? E_per_module * modsPerOptimizer
        : E_per_module;

      let energyLossKWh = n * E_base * SR * periodYears;
      energyLossKWh = Math.min(energyLossKWh, E_total * periodYears);

      const revenueLoss = energyLossKWh * tariff;
      return { ...fault, energyLossKWh, revenueLoss };
    });

    const totalEnergyLoss = faultResults.reduce((sum, r) => sum + r.energyLossKWh, 0);
    const totalRevenueLoss = faultResults.reduce((sum, r) => sum + r.revenueLoss, 0);

    const paretoData = faultResults
      .filter((r) => r.energyLossKWh > 0)
      .sort((a, b) => b.energyLossKWh - a.energyLossKWh)
      .map((r) => ({
        label: r.label,
        energyLossKWh: r.energyLossKWh,
        revenueLoss: r.revenueLoss,
        pct: totalEnergyLoss > 0 ? (r.energyLossKWh / totalEnergyLoss) * 100 : 0,
        color: r.color,
      }));

    let cumPct = 0;
    const paretoWithCum = paretoData.map((item) => {
      cumPct += item.pct;
      return { ...item, cumPct };
    });

    return { E_total, faultResults, totalEnergyLoss, totalRevenueLoss, paretoData: paretoWithCum };
  }, [dcCapacityKWp, expectedYield, modulePowerWp, totalModules, stringsCount, tariff, faults, periodYears, modsPerOptimizer]);

  // ── Fault table updater ──
  const updateFault = (index: number, field: keyof FaultRow, value: string | number) => {
    setFaults((prev) => prev.map((f, i) => (i === index ? { ...f, [field]: value } : f)));
  };

  // ── Sync from Legend ──
  const handleSyncFromLegend = () => {
    const countMap = new Map<string, number>();
    for (const ind of indications) {
      const typeInfo = INDICATION_TYPES[ind.type as keyof typeof INDICATION_TYPES];
      if (typeInfo?.counted) {
        countMap.set(ind.type, (countMap.get(ind.type) || 0) + 1);
      }
    }
    setFaults((prev) =>
      prev.map((fault) => {
        if (fault.id === 'Custom') return fault;
        const count = countMap.get(fault.id) || 0;
        return { ...fault, count };
      })
    );
  };

  // ── Export CSV ──
  const handleExportCSV = () => {
    const configLines = [
      '# PV Fault Impact Calculator Report',
      `# DC Capacity: ${dcCapacityKWp} kWp`,
      `# Expected Yield: ${expectedYield} kWh/kWp/yr`,
      `# Module Power: ${modulePowerWp} Wp`,
      `# Tariff: ${currency}${tariff}/kWh`,
      `# Total Modules: ${totalModules}`,
      `# Strings: ${stringsCount}`,
      `# Period: ${periodValue} ${periodUnit}`,
      '',
    ].join('\n');

    const header = `Fault Type,Count,Category,Scope,Impact,Energy Loss (kWh),Revenue Loss (${currency})\n`;
    const rows = results.faultResults
      .map((r) =>
        `"${r.label}",${r.count},"${r.category}","${r.scope}","${r.impact}",${r.energyLossKWh.toFixed(1)},${r.revenueLoss.toFixed(2)}`
      )
      .join('\n');
    const summary = `\n"TOTAL",,,,,${results.totalEnergyLoss.toFixed(1)},${results.totalRevenueLoss.toFixed(2)}`;

    downloadBlob(configLines + header + rows + summary, `fault-impact-${new Date().toISOString().slice(0, 10)}.csv`, 'text/csv;charset=utf-8;');
  };

  // ── Export JSON ──
  const handleExportJSON = () => {
    const report = {
      generatedAt: new Date().toISOString(),
      systemConfig: { dcCapacityKWp, expectedYield, modulePowerWp, cellsPerModule, bypassDiodes, moduleType },
      topology: { totalModules, stringsCount, mpptCount, inverterCount },
      financial: { tariff, currency },
      period: { value: periodValue, unit: periodUnit, asYears: periodYears },
      faults: results.faultResults.map((r) => ({
        type: r.label, count: r.count, category: r.category, scope: r.scope,
        impact: r.impact,
        energyLossKWh: +r.energyLossKWh.toFixed(1),
        revenueLoss: +r.revenueLoss.toFixed(2),
      })),
      totals: {
        totalEnergyLossKWh: +results.totalEnergyLoss.toFixed(1),
        totalRevenueLoss: +results.totalRevenueLoss.toFixed(2),
        baselineEnergyKWh: results.E_total,
        lossPercentage: +((results.totalEnergyLoss / results.E_total) * 100).toFixed(2),
      },
      pareto: results.paretoData.map((p) => ({
        type: p.label,
        energyLossKWh: +p.energyLossKWh.toFixed(1),
        contributionPct: +p.pct.toFixed(1),
        cumulativePct: +p.cumPct.toFixed(1),
      })),
    };
    downloadBlob(JSON.stringify(report, null, 2), `fault-impact-${new Date().toISOString().slice(0, 10)}.json`, 'application/json');
  };

  // ── Calculate modules from capacity ──
  const handleCalcModules = () => {
    if (modulePowerWp > 0) {
      setTotalModules(Math.round((dcCapacityKWp * 1000) / modulePowerWp));
    }
  };

  const baselineForPeriod = results.E_total * periodYears;
  const lossPct = baselineForPeriod > 0 ? (results.totalEnergyLoss / baselineForPeriod) * 100 : 0;

  // ── Render ──
  return (
    <div className="cp-overlay" onClick={onClose}>
      <div className="cp-container" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="cp-header">
          <div className="cp-header__left">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="2" width="16" height="20" rx="2"/>
              <line x1="8" y1="6" x2="16" y2="6"/>
              <line x1="8" y1="10" x2="10" y2="10"/>
              <line x1="14" y1="10" x2="16" y2="10"/>
              <line x1="8" y1="14" x2="10" y2="14"/>
              <line x1="14" y1="14" x2="16" y2="14"/>
              <line x1="8" y1="18" x2="10" y2="18"/>
              <line x1="14" y1="18" x2="16" y2="18"/>
            </svg>
            <span className="cp-header__title">PV Fault Impact Calculator</span>
          </div>
          <button className="cp-close-btn" onClick={onClose} type="button" title="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="cp-body">

          {/* Config Grid */}
          <div className="cp-config">
            <div className="cp-config__group">
              <div className="cp-config__group-title">System Config</div>
              <label className="cp-field">
                <span className="cp-field__label">DC Cap (kWp)</span>
                <input type="number" className="cp-field__input" value={dcCapacityKWp} min={0}
                  onChange={(e) => setDcCapacityKWp(+e.target.value)} />
              </label>
              <label className="cp-field">
                <span className="cp-field__label">Yield (kWh/kWp)</span>
                <input type="number" className="cp-field__input" value={expectedYield} min={0}
                  onChange={(e) => setExpectedYield(+e.target.value)} />
              </label>
            </div>

            <div className="cp-config__group">
              <div className="cp-config__group-title">Module Config</div>
              <label className="cp-field">
                <span className="cp-field__label">Power (Wp)</span>
                <input type="number" className="cp-field__input" value={modulePowerWp} min={0}
                  onChange={(e) => setModulePowerWp(+e.target.value)} />
              </label>
              <label className="cp-field">
                <span className="cp-field__label">Cells/Mod</span>
                <input type="number" className="cp-field__input" value={cellsPerModule} min={0}
                  onChange={(e) => setCellsPerModule(+e.target.value)} />
              </label>
              <label className="cp-field">
                <span className="cp-field__label">Diodes</span>
                <input type="number" className="cp-field__input" value={bypassDiodes} min={0}
                  onChange={(e) => setBypassDiodes(+e.target.value)} />
              </label>
              <label className="cp-field">
                <span className="cp-field__label">Type</span>
                <select className="cp-field__input" value={moduleType} onChange={(e) => setModuleType(e.target.value)}>
                  {MODULE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
            </div>

            <div className="cp-config__group">
              <div className="cp-config__group-title">Topology</div>
              <label className="cp-field">
                <span className="cp-field__label">
                  Modules
                  <button type="button" className="cp-calc-btn" onClick={handleCalcModules} title="Calculate from capacity / module power">
                    calc
                  </button>
                </span>
                <input type="number" className="cp-field__input" value={totalModules} min={0}
                  onChange={(e) => setTotalModules(+e.target.value)} />
              </label>
              <label className="cp-field">
                <span className="cp-field__label">Strings</span>
                <input type="number" className="cp-field__input" value={stringsCount} min={0}
                  onChange={(e) => setStringsCount(+e.target.value)} />
              </label>
              <label className="cp-field">
                <span className="cp-field__label">MPPTs</span>
                <input type="number" className="cp-field__input" value={mpptCount} min={0}
                  onChange={(e) => setMpptCount(+e.target.value)} />
              </label>
              <label className="cp-field">
                <span className="cp-field__label">Inverters</span>
                <input type="number" className="cp-field__input" value={inverterCount} min={0}
                  onChange={(e) => setInverterCount(+e.target.value)} />
              </label>
              <label className="cp-field">
                <span className="cp-field__label">Mods/Opt</span>
                <select className="cp-field__input" value={modsPerOptimizer}
                  onChange={(e) => setModsPerOptimizer(+e.target.value)}>
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                </select>
              </label>
            </div>

            <div className="cp-config__group">
              <div className="cp-config__group-title">Financial</div>
              <label className="cp-field">
                <span className="cp-field__label">Tariff</span>
                <input type="number" className="cp-field__input" value={tariff} min={0} step={0.01}
                  onChange={(e) => setTariff(+e.target.value)} />
              </label>
              <label className="cp-field">
                <span className="cp-field__label">Currency</span>
                <select className="cp-field__input" value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>
          </div>

          {/* Toolbar */}
          <div className="cp-toolbar">
            <button type="button" className="cp-btn cp-btn--primary" onClick={handleSyncFromLegend}>
              Sync from Legend
            </button>
            <div className="cp-toolbar__spacer" />
            <div className="cp-period">
              <span className="cp-period__label">Period:</span>
              <input type="number" className="cp-period__input" value={periodValue} min={1}
                onChange={(e) => setPeriodValue(Math.max(1, +e.target.value))} />
              <select className="cp-period__select" value={periodUnit}
                onChange={(e) => setPeriodUnit(e.target.value as TimeUnit)}>
                {TIME_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="cp-toolbar__spacer" />
            <button type="button" className="cp-btn" onClick={handleExportCSV}>
              Export CSV
            </button>
            <button type="button" className="cp-btn" onClick={handleExportJSON}>
              Export JSON
            </button>
          </div>

          {/* Fault Table */}
          <div className="cp-table-wrap">
            <table className="cp-table">
              <thead>
                <tr>
                  <th className="cp-th cp-th--num">#</th>
                  <th className="cp-th cp-th--type">Fault Type</th>
                  <th className="cp-th cp-th--count">Count</th>
                  <th className="cp-th cp-th--cat">Category</th>
                  <th className="cp-th cp-th--scope">Scope</th>
                  <th className="cp-th cp-th--impact">Impact</th>
                  <th className="cp-th cp-th--loss">Energy Loss</th>
                  <th className="cp-th cp-th--rev">Revenue Loss</th>
                </tr>
              </thead>
              <tbody>
                {results.faultResults.map((fault, i) => (
                  <tr key={fault.id}
                    className={`cp-row${selectedFaultIndex === i ? ' cp-row--selected' : ''}`}
                    onClick={() => setSelectedFaultIndex(selectedFaultIndex === i ? null : i)}>
                    <td className="cp-td cp-td--num">{i + 1}</td>
                    <td className="cp-td cp-td--type">
                      <span className="cp-fault-dot" style={{ backgroundColor: fault.color }} />
                      {fault.label}
                    </td>
                    <td className="cp-td cp-td--count">
                      <input type="number" className="cp-cell-input" value={fault.count} min={0}
                        onChange={(e) => updateFault(i, 'count', +e.target.value)} />
                    </td>
                    <td className="cp-td cp-td--cat">
                      <select className="cp-cell-select" value={fault.category}
                        onChange={(e) => updateFault(i, 'category', e.target.value)}>
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td className="cp-td cp-td--scope">
                      <select className="cp-cell-select" value={fault.scope}
                        onChange={(e) => updateFault(i, 'scope', e.target.value)}>
                        {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="cp-td cp-td--impact">
                      <select className="cp-cell-select" value={fault.impact}
                        onChange={(e) => updateFault(i, 'impact', e.target.value)}>
                        {IMPACTS.map((im) => <option key={im} value={im}>{im}</option>)}
                      </select>
                    </td>
                    <td className="cp-td cp-td--loss">
                      {fault.energyLossKWh > 0 ? formatNum(fault.energyLossKWh) + ' kWh' : '-'}
                    </td>
                    <td className="cp-td cp-td--rev">
                      {fault.revenueLoss > 0 ? currency + formatNum(fault.revenueLoss, 2) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Logic Breakdown */}
          {selectedFaultIndex !== null && (() => {
            const sf = results.faultResults[selectedFaultIndex];
            if (!sf) return null;
            const E_mod = (modulePowerWp / 1000) * expectedYield;
            const modsPerStr = stringsCount > 0 ? totalModules / stringsCount : 0;
            const E_str = E_mod * modsPerStr;
            const SR = SR_MAP[sf.impact] ?? 0;
            const srPct = (SR * 100).toFixed(0);

            if (sf.category === 'Risk Only') {
              return (
                <div className="cp-logic">
                  <div className="cp-logic__header">
                    <span className="cp-logic__dot" style={{ backgroundColor: sf.color }} />
                    <span>{sf.label}</span>
                    <span className="cp-logic__tag">· Risk Only — no energy loss calculated (monitoring/preventive action only)</span>
                  </div>
                </div>
              );
            }

            if (sf.count === 0) {
              return (
                <div className="cp-logic">
                  <div className="cp-logic__header">
                    <span className="cp-logic__dot" style={{ backgroundColor: sf.color }} />
                    <span>{sf.label}</span>
                    <span className="cp-logic__tag">· Count is 0 — set a fault count to see the calculation</span>
                  </div>
                </div>
              );
            }

            const isString = sf.scope === 'String';
            const isOpt = sf.id === 'Opt.OC' || sf.id === 'Opt.SC';
            const E_opt = E_mod * modsPerOptimizer;
            const E_base = isString ? E_str : isOpt ? E_opt : E_mod;
            const loss = sf.energyLossKWh;
            const rev = sf.revenueLoss;

            return (
              <div className="cp-logic">
                <div className="cp-logic__header">
                  <span className="cp-logic__dot" style={{ backgroundColor: sf.color }} />
                  <span>{sf.label}</span>
                  <span className="cp-logic__tag">· {sf.scope} scope{isOpt ? ` (${modsPerOptimizer} mod/opt)` : ''} · Impact: {sf.impact} ({srPct}%) · {sf.category}</span>
                </div>
                <div className="cp-logic__steps">
                  <div className="cp-logic__line">
                    <span className="cp-logic__label">E_module</span>
                    <span className="cp-logic__expr">= {modulePowerWp} Wp ÷ 1000 × {expectedYield} kWh/kWp</span>
                    <span className="cp-logic__result">= {formatNum(E_mod, 1)} kWh/yr</span>
                  </div>
                  {isOpt && modsPerOptimizer > 1 && (
                    <div className="cp-logic__line">
                      <span className="cp-logic__label">E_optimizer</span>
                      <span className="cp-logic__expr">= {formatNum(E_mod, 1)} × {modsPerOptimizer} mods/opt</span>
                      <span className="cp-logic__result">= {formatNum(E_opt, 1)} kWh/yr</span>
                    </div>
                  )}
                  {isString && (
                    <>
                      <div className="cp-logic__line">
                        <span className="cp-logic__label">Mods/Str</span>
                        <span className="cp-logic__expr">= {totalModules} ÷ {stringsCount}</span>
                        <span className="cp-logic__result">= {modsPerStr.toFixed(1)}</span>
                      </div>
                      <div className="cp-logic__line">
                        <span className="cp-logic__label">E_string</span>
                        <span className="cp-logic__expr">= {formatNum(E_mod, 1)} × {modsPerStr.toFixed(1)}</span>
                        <span className="cp-logic__result">= {formatNum(E_str, 1)} kWh/yr</span>
                      </div>
                    </>
                  )}
                  <div className="cp-logic__line">
                    <span className="cp-logic__label">ΔE</span>
                    <span className="cp-logic__expr">= {sf.count} × {formatNum(E_base, 1)} kWh × {SR.toFixed(2)} × {periodStr}</span>
                    <span className="cp-logic__result">= {formatNum(loss, 1)} kWh</span>
                  </div>
                  <div className="cp-logic__line">
                    <span className="cp-logic__label">Revenue</span>
                    <span className="cp-logic__expr">= {formatNum(loss, 1)} kWh × {currency}{tariff}/kWh</span>
                    <span className="cp-logic__result">= {currency}{formatNum(rev, 2)}</span>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Summary + Pareto side by side */}
          <div className="cp-bottom">
            <div className="cp-summary">
              <div className="cp-summary__item">
                <span className="cp-summary__label">Energy Loss / {periodStr}</span>
                <span className="cp-summary__value cp-summary__value--loss">
                  {formatNum(results.totalEnergyLoss)} kWh
                </span>
                <span className="cp-summary__sub">{lossPct.toFixed(2)}% of baseline ({formatNum(baselineForPeriod)} kWh / {periodStr})</span>
              </div>
              <div className="cp-summary__item">
                <span className="cp-summary__label">Revenue Loss / {periodStr}</span>
                <span className="cp-summary__value cp-summary__value--money">
                  {currency}{formatNum(results.totalRevenueLoss, 2)}
                </span>
                <span className="cp-summary__sub">at {currency}{tariff}/kWh</span>
              </div>
            </div>

            <div className="cp-pareto">
              <div className="cp-pareto__title">Pareto Analysis</div>
              {results.paretoData.length === 0 ? (
                <div className="cp-empty">No faults with losses</div>
              ) : (
                <div className="cp-pareto__chart">
                  {results.paretoData.map((item) => {
                    const maxLoss = results.paretoData[0].energyLossKWh;
                    const barWidth = maxLoss > 0 ? (item.energyLossKWh / maxLoss) * 100 : 0;
                    return (
                      <div key={item.label} className="cp-pareto__row">
                        <span className="cp-pareto__label">{item.label}</span>
                        <div className="cp-pareto__bar-track">
                          <div
                            className="cp-pareto__bar-fill"
                            style={{ width: `${barWidth}%`, backgroundColor: item.color }}
                          />
                        </div>
                        <span className="cp-pareto__pct">{item.pct.toFixed(1)}%</span>
                        <span className="cp-pareto__value">
                          {item.energyLossKWh >= 1000
                            ? `${(item.energyLossKWh / 1000).toFixed(1)}k`
                            : formatNum(item.energyLossKWh)} kWh
                        </span>
                        <span className="cp-pareto__revenue">
                          {currency}{formatNum(item.revenueLoss, 0)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
